import { useCallback } from "react";
import { NavigateOptions, To, useNavigate } from "react-router-dom";

import { useScopedHref } from "./useScopedHref";

const scopeTo = (to: To, scopeHref: (path: string) => string): To => {
  if (typeof to === "string") return scopeHref(to);
  if (to.pathname) return { ...to, pathname: scopeHref(to.pathname) };
  return to;
};

/**
 * Drop-in replacement for `useNavigate()` that scope-prefixes internal paths
 * (e.g. `"/users"` → `"/platform/:p/entity/:o/users"`) when a platform +
 * organization are available. External, anchor, already-scoped, and relative
 * paths pass through unchanged.
 *
 * Mirrors react-router's overloaded signature: pass a number to go back/forward
 * through history, or pass a `To` (string or partial path) to navigate.
 */
export const useScopedNavigate = () => {
  const navigate = useNavigate();
  const scopedHref = useScopedHref();

  return useCallback(
    (to: To | number, options?: NavigateOptions) => {
      if (typeof to === "number") {
        navigate(to);
        return;
      }
      navigate(scopeTo(to, scopedHref), options);
    },
    [navigate, scopedHref]
  );
};
